import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Loader2,
  ExternalLink,
  RefreshCw,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  X,
  FileText,
  FileImage,
  FileSpreadsheet,
  FileType,
  File as FileIcon,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
} from "lucide-react";
import {
  api,
  type AttachmentDetail,
  type PageDetail,
} from "../lib/api.js";
import { Button } from "./ui/button.js";
import { cn } from "../lib/cn.js";
import type { JobActivity } from "../lib/syncEvents.js";
import { useWsFrames } from "../lib/ws-client.js";

type Tab = "markdown" | "attachments" | "raw";

interface PageDetailPanelProps {
  pageId: string;
  /**
   * Full page metadata from the selection. Needed so the "Sync this page"
   * button can build a page-scope SyncScope even when the page is not yet
   * in the local corpus (detailQ returns 404 in that case and its data is
   * undefined). Without this, the sync mutation would throw synchronously
   * on an unsynced page and silently fail.
   */
  pageContext: {
    siteId: string;
    notebookId: string;
    notebookName: string;
    sectionId: string;
    sectionName: string;
    title: string;
  };
  onClose: () => void;
  liveJob?: JobActivity;
}

export function PageDetailPanel({
  pageId,
  pageContext,
  onClose,
  liveJob,
}: PageDetailPanelProps) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("markdown");

  const detailQ = useQuery({
    queryKey: ["corpusPage", pageId],
    queryFn: () => api.getCorpusPage(pageId),
    retry: (count, err) => {
      // 404 means "not synced" — don't retry
      if (err instanceof Error && err.message.startsWith("404")) return false;
      return count < 2;
    },
    refetchInterval: liveJob ? 3_000 : false,
  });

  // Single sync mutation. Used for both first-sync ("not yet in corpus") and
  // re-sync paths — the lazy decision tree on the backend handles each
  // case (cache-hit on unchanged pages costs ~0 Graph requests).
  const syncMut = useMutation({
    mutationFn: () => {
      // Prefer corpus-side data when it's present (reflects any rename);
      // fall back to the selection-time pageContext so the button works
      // for pages not yet in the local corpus (detailQ is 404 in that case).
      const d = detailQ.data;
      return api.sync({
        type: "page",
        id: pageId,
        siteId: d?.siteId ?? pageContext.siteId,
        notebookId: d?.notebookId ?? pageContext.notebookId,
        notebookName: d?.notebook ?? pageContext.notebookName,
        sectionId: d?.sectionId ?? pageContext.sectionId,
        sectionName: d?.section ?? pageContext.sectionName,
        label: d?.title ?? pageContext.title,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["corpusPage", pageId] });
      void qc.invalidateQueries({ queryKey: ["corpusStatus"] });
    },
    onError: (err) => {
      // Don't alert — the most common failure here is a 409 conflict (a
      // sync is already in flight for the scope), and the SPA banner
      // already shows that running job. Anything else lands in console.
      console.error("page sync failed:", err);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.deleteCorpusPage(pageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["corpusStatus"] });
      onClose();
    },
  });

  const detail = detailQ.data;
  const notFound =
    detailQ.isError &&
    detailQ.error instanceof Error &&
    detailQ.error.message.startsWith("404");

  // Refetch the panel's data whenever a sync that could have rewritten this
  // page completes. syncMut.onSuccess invalidates immediately on
  // 202-accepted, but at that moment the worker hasn't run yet — the
  // refetch returns stale data and nothing nudges it again. Subscribing
  // here covers all three scopes that touch this page's S3 markdown.
  useWsFrames((frame) => {
    if (frame.kind !== "sync_progress") return;
    const p = frame.progress as
      | { status?: string; scopeType?: string; scopeId?: string }
      | undefined;
    if (!p || p.status !== "completed") return;
    const ours =
      (p.scopeType === "page" && p.scopeId === pageId) ||
      (p.scopeType === "section" && p.scopeId === detail?.sectionId) ||
      (p.scopeType === "notebook" && p.scopeId === detail?.notebookId);
    if (ours) {
      void qc.invalidateQueries({ queryKey: ["corpusPage", pageId] });
    }
  });

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
        <div className="min-w-0 flex-1">
          {detailQ.isLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading page...
            </div>
          )}
          {notFound && (
            <NotInCorpusHeader
              onSync={() => syncMut.mutate()}
              syncing={syncMut.isPending || !!liveJob}
              liveJob={liveJob}
            />
          )}
          {detail && (
            <DetailHeader
              detail={detail}
              liveJob={liveJob}
              onSync={() => syncMut.mutate()}
              syncing={syncMut.isPending || !!liveJob}
              onDelete={() => {
                if (confirm(`Remove "${detail.title}" from local corpus?`))
                  deleteMut.mutate();
              }}
              deleting={deleteMut.isPending}
            />
          )}
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-slate-400 hover:bg-slate-100"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Error banner — appears when the page row has status="error".
          Renders the friendly classified message + 8-char trace ID
          (copyable for support) + Microsoft Graph request-id (copyable
          for upstream support). Sticky until a successful re-sync clears
          the error fields on the row. */}
      {detail && detail.status === "error" && detail.errorMessage && (
        <ErrorBanner detail={detail} />
      )}

      {/* Live banner — appears when a sync job touching this page's
          scope is in flight. The page-level "syncing now" message is
          tied to the section-level liveJob; the section's
          SyncProgressBanner shows full progress detail. */}
      {liveJob && (
        <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-xs text-blue-900">
          <div className="flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span className="font-medium">Sync in progress for this section</span>
            {liveJob.pagesTotal > 0 && (
              <span className="text-blue-700">
                · {liveJob.pagesDone}/{liveJob.pagesTotal} pages
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      {detail && (
        <>
          <div className="flex gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5 text-xs">
            <TabBtn active={tab === "markdown"} onClick={() => setTab("markdown")}>
              Page markdown
            </TabBtn>
            <TabBtn
              active={tab === "attachments"}
              onClick={() => setTab("attachments")}
            >
              Attachments ({detail.attachments.length})
            </TabBtn>
            <TabBtn active={tab === "raw"} onClick={() => setTab("raw")}>
              Raw meta
            </TabBtn>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-4">
            {tab === "markdown" && <MarkdownTab markdown={detail.markdown} />}
            {tab === "attachments" && (
              <AttachmentsTab attachments={detail.attachments} />
            )}
            {tab === "raw" && (
              <pre className="overflow-x-auto rounded bg-slate-50 p-3 text-[11px] text-slate-700">
                {detail.metaJson ?? "(no meta.json)"}
              </pre>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// Error banner (status="error") — shown ABOVE the tabs/content
// ============================================================

function ErrorBanner({ detail }: { detail: PageDetail }) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const copy = (label: string, value: string) => {
    void navigator.clipboard.writeText(value);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950">
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <span className="font-medium">{detail.errorMessage}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-amber-800">
            {detail.errorId && (
              <button
                type="button"
                onClick={() => copy("errorId", detail.errorId!)}
                className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white/60 px-1.5 py-0.5 hover:bg-white"
                title="Copy trace ID for support"
              >
                <span className="text-amber-600">trace</span>
                <code className="font-mono">{detail.errorId}</code>
                {copiedField === "errorId" ? (
                  <Check className="h-3 w-3 text-emerald-600" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            )}
            {detail.errorProviderTraceId && (
              <button
                type="button"
                onClick={() => copy("provider", detail.errorProviderTraceId!)}
                className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white/60 px-1.5 py-0.5 hover:bg-white"
                title="Copy Microsoft / OpenAI request-id for forwarding to upstream support"
              >
                <span className="text-amber-600">upstream</span>
                <code className="truncate font-mono">{detail.errorProviderTraceId.slice(0, 18)}…</code>
                {copiedField === "provider" ? (
                  <Check className="h-3 w-3 text-emerald-600" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            )}
            {detail.errorAt && (
              <span className="text-amber-700">{relTime(detail.errorAt)}</span>
            )}
            {detail.webUrl && (
              <a
                href={detail.webUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-amber-800 underline-offset-2 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                Open in OneNote
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Header bits
// ============================================================

function DetailHeader({
  detail,
  liveJob,
  onSync,
  syncing,
  onDelete,
  deleting,
}: {
  detail: PageDetail;
  liveJob?: JobActivity;
  onSync: () => void;
  syncing: boolean;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <div>
      <h3 className="truncate text-base font-semibold text-slate-900">
        {detail.title}
      </h3>
      <p className="mt-0.5 truncate text-xs text-slate-500">
        {detail.notebook} / {detail.section}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <StatusBadge
          status={detail.status}
          isStale={detail.isStale}
          isLive={!!liveJob}
        />
        <span className="text-[11px] text-slate-500">
          Synced {relTime(detail.syncedAt)} · Modified in OneNote{" "}
          {relTime(detail.graphModifiedAt)}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={onSync}
            disabled={syncing}
            title={
              detail.isStale
                ? "Re-sync (page changed in OneNote)"
                : "Re-sync this page from OneNote"
            }
          >
            {syncing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            <span className="ml-1">Sync</span>
          </Button>
          {detail.webUrl && (
            <a
              href={detail.webUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 px-2 text-xs text-slate-700 hover:bg-slate-50"
            >
              <ExternalLink className="h-3 w-3" />
              OneNote
            </a>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
            onClick={onDelete}
            disabled={deleting}
          >
            {deleting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            <span className="ml-1">Remove</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

function NotInCorpusHeader({
  onSync,
  syncing,
  liveJob,
}: {
  onSync: () => void;
  syncing: boolean;
  liveJob?: JobActivity;
}) {
  return (
    <div>
      <h3 className="text-base font-semibold text-slate-900">
        Page not yet synced
      </h3>
      <p className="mt-0.5 text-xs text-slate-500">
        {liveJob
          ? "A sync that includes this page is currently running."
          : "Sync this page to add it to the local corpus."}
      </p>
      {!liveJob && (
        <Button
          size="sm"
          variant="outline"
          className="mt-2 h-7 px-2 text-xs"
          onClick={onSync}
          disabled={syncing}
        >
          {syncing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          <span className="ml-1">Sync this page</span>
        </Button>
      )}
    </div>
  );
}

function StatusBadge({
  status,
  isStale,
  isLive,
}: {
  status: string;
  isStale: boolean;
  isLive: boolean;
}) {
  if (isLive)
    return (
      <Badge color="blue" icon={<Loader2 className="h-3 w-3 animate-spin" />}>
        Syncing
      </Badge>
    );
  if (status === "syncing")
    return (
      <Badge color="blue" icon={<Loader2 className="h-3 w-3 animate-spin" />}>
        Syncing
      </Badge>
    );
  if (status === "partial")
    return (
      <Badge color="amber" icon={<AlertCircle className="h-3 w-3" />}>
        Partial (some attachments failed)
      </Badge>
    );
  if (isStale)
    return (
      <Badge color="amber" icon={<AlertCircle className="h-3 w-3" />}>
        Stale
      </Badge>
    );
  if (status === "ok")
    return (
      <Badge color="emerald" icon={<CheckCircle2 className="h-3 w-3" />}>
        Synced
      </Badge>
    );
  return (
    <Badge color="slate" icon={<Clock className="h-3 w-3" />}>
      {status}
    </Badge>
  );
}

function Badge({
  color,
  icon,
  children,
}: {
  color: "blue" | "emerald" | "amber" | "red" | "slate";
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const map = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        map[color],
      )}
    >
      {icon}
      {children}
    </span>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-md px-2 py-1",
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700",
      )}
    >
      {children}
    </button>
  );
}

// ============================================================
// Tab content
// ============================================================

function MarkdownTab({ markdown }: { markdown: string }) {
  return (
    <div className="prose prose-sm prose-slate max-w-none prose-p:my-1.5 prose-li:my-0 prose-pre:rounded prose-pre:bg-slate-50 prose-pre:p-2 prose-pre:text-[11px] prose-pre:text-slate-800 prose-code:rounded prose-code:bg-slate-100 prose-code:px-1 prose-code:text-slate-800 prose-table:my-2 prose-table:border-collapse prose-table:text-xs prose-th:border prose-th:border-slate-200 prose-th:bg-slate-50 prose-th:px-2 prose-th:py-1 prose-td:border prose-td:border-slate-200 prose-td:px-2 prose-td:py-1">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
    </div>
  );
}

function AttachmentsTab({ attachments }: { attachments: AttachmentDetail[] }) {
  if (attachments.length === 0) {
    return (
      <p className="text-sm text-slate-500">This page has no attachments.</p>
    );
  }
  return (
    <ul className="space-y-2">
      {attachments.map((a) => (
        <AttachmentRow key={a.resourceId} attachment={a} />
      ))}
    </ul>
  );
}

function AttachmentRow({ attachment }: { attachment: AttachmentDetail }) {
  const [open, setOpen] = useState(false);
  const [fullText, setFullText] = useState<string | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);

  const loadFull = async () => {
    if (fullText !== null) return;
    setLoadingFull(true);
    try {
      const t = await api.getAttachmentText(attachment.resourceId);
      setFullText(t);
    } catch (err) {
      setFullText(`(failed to load: ${err instanceof Error ? err.message : err})`);
    } finally {
      setLoadingFull(false);
    }
  };

  const expandable = attachment.status === "ok" && attachment.textPreview;

  return (
    <li className="rounded border border-slate-200">
      <button
        disabled={!expandable}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) void loadFull();
        }}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
          expandable ? "hover:bg-slate-50" : "cursor-default",
        )}
      >
        {expandable ? (
          open ? (
            <ChevronDown className="h-3 w-3 text-slate-400" />
          ) : (
            <ChevronRight className="h-3 w-3 text-slate-400" />
          )
        ) : (
          <span className="w-3" />
        )}
        <KindIcon kind={attachment.kind} />
        <span className="truncate font-medium text-slate-900">
          {attachment.filename}
        </span>
        <span className="text-xs text-slate-400">{attachment.kind}</span>
        <span className="ml-auto">
          <AttStatus status={attachment.status} />
        </span>
      </button>

      {attachment.error && (
        <p className="border-t border-slate-100 px-3 py-1.5 text-xs text-red-700">
          {attachment.error}
        </p>
      )}

      {open && (
        <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 text-xs">
          {loadingFull && fullText === null ? (
            <span className="flex items-center gap-1 text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> loading…
            </span>
          ) : (
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap text-[11px] leading-snug text-slate-800">
              {fullText ?? attachment.textPreview ?? ""}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

function AttStatus({ status }: { status: string }) {
  if (status === "ok")
    return (
      <Badge color="emerald" icon={<CheckCircle2 className="h-3 w-3" />}>
        ok
      </Badge>
    );
  if (status === "error")
    return (
      <Badge color="red" icon={<AlertCircle className="h-3 w-3" />}>
        error
      </Badge>
    );
  if (status === "unsupported")
    return (
      <Badge color="slate" icon={<AlertCircle className="h-3 w-3" />}>
        unsupported
      </Badge>
    );
  return (
    <Badge color="blue" icon={<Loader2 className="h-3 w-3 animate-spin" />}>
      {status}
    </Badge>
  );
}

function KindIcon({ kind }: { kind: string }) {
  const cls = "h-4 w-4 shrink-0 text-slate-500";
  if (kind === "image") return <FileImage className={cls} />;
  if (kind === "pdf") return <FileType className={cls} />;
  if (kind === "xlsx") return <FileSpreadsheet className={cls} />;
  if (kind === "docx" || kind === "pptx") return <FileText className={cls} />;
  return <FileIcon className={cls} />;
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}


